import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Typography,
  Card,
  CardContent,
  Chip,
  Fab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import { usePermissions } from '../firebase/PermissionProvider';
import { useNotification } from '../providers/NotificationProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
  STANDARD_FAB_CTA_SX,
} from '../constants/buttonStyles';
import { useEventList, EventListRow, EventUpsertInput } from '../hooks/useEventList';
import type { EventResultFormat } from '../hooks/useEventManagement';

/**
 * イベント一覧 (マスタ管理) ページ。
 *
 * mst_event をカード一覧で表示し、カード選択で申込状況ページ (/event-management/:eventId) へ遷移する。
 * 新規登録・編集はダイアログで行う。event_code は申込サイト CGI (DEFAULT_EVENT_CODE) と
 * 連動する運用キーのため、新規登録時のみ入力でき、以後は変更不可。削除機能は設けない
 * (公開状態は is_active で運用する)。
 *
 * 権限の住み分け (2段階)
 *   menu.eventmanagement.view … メニュー表示+閲覧 (実行委員会メンバー)
 *   event.master.edit         … イベントマスタの新規登録・編集 (管理者のみ。Fab・鉛筆の表示制御)
 */

const yen = (v: number | null | undefined): string => (v == null ? '未定' : `¥${Number(v).toLocaleString()}`);

// ダイアログの編集値 (入力中はすべて文字列で保持し、送信時に整形する)
type FormState = {
  event_code: string;
  event_name: string;
  event_type: string;
  event_date: string;
  venue: string;
  fee_regular: string;
  fee_prepaid: string;
  entry_start: string;
  entry_end: string;
  is_active: boolean;
  chat_webhook_url: string;
  group_unit_label: string;
  position_unit_label: string;
  // 成績の形式 (空文字は成績を記録しない)
  result_format_code: string;
};

const emptyForm: FormState = {
  event_code: '',
  event_name: '',
  event_type: '',
  event_date: '',
  venue: '',
  fee_regular: '',
  fee_prepaid: '',
  entry_start: '',
  entry_end: '',
  is_active: true,
  chat_webhook_url: '',
  group_unit_label: '卓',
  position_unit_label: '席',
  result_format_code: '',
};

function toForm(row: EventListRow): FormState {
  return {
    event_code: row.event_code,
    event_name: row.event_name,
    event_type: row.event_type ?? '',
    event_date: row.event_date ?? '',
    venue: row.venue ?? '',
    fee_regular: row.fee_regular == null ? '' : String(row.fee_regular),
    fee_prepaid: row.fee_prepaid == null ? '' : String(row.fee_prepaid),
    entry_start: row.entry_start ?? '',
    entry_end: row.entry_end ?? '',
    is_active: row.is_active,
    chat_webhook_url: row.chat_webhook_url ?? '',
    group_unit_label: row.group_unit_label ?? '卓',
    position_unit_label: row.position_unit_label ?? '席',
    result_format_code: row.result_format_code ?? '',
  };
}

function EventEditDialog({
  open,
  initial,
  resultFormats,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: EventListRow | null;
  resultFormats: EventResultFormat[];
  saving: boolean;
  onClose: () => void;
  onSave: (data: EventUpsertInput) => void;
}) {
  const isEdit = initial != null;
  const [form, setForm] = useState<FormState>(emptyForm);

  // 開くたびに編集対象で初期化する (新規は空フォーム)
  useEffect(() => {
    if (open) setForm(initial ? toForm(initial) : emptyForm);
  }, [open, initial]);

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = () => {
    const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));
    const str = (s: string): string | null => (s.trim() === '' ? null : s.trim());
    onSave({
      ...(isEdit ? { id: initial!.id } : { event_code: form.event_code.trim() }),
      event_name: form.event_name.trim(),
      event_type: str(form.event_type),
      event_date: str(form.event_date),
      venue: str(form.venue),
      fee_regular: num(form.fee_regular),
      fee_prepaid: num(form.fee_prepaid),
      entry_start: str(form.entry_start),
      entry_end: str(form.entry_end),
      is_active: form.is_active,
      chat_webhook_url: str(form.chat_webhook_url),
      group_unit_label: str(form.group_unit_label),
      position_unit_label: str(form.position_unit_label),
      result_format_code: str(form.result_format_code),
    });
  };

  const fieldSx = { '& .MuiInputBase-input': { fontSize: '13px' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, color: blueGrey[600], borderBottom: '1px solid #f1f5f9' }}>
        {isEdit ? <Icons.Edit sx={{ fontSize: '18px' }} /> : <Icons.Add sx={{ fontSize: '18px' }} />}
        {isEdit ? 'イベントを編集' : 'イベントを新規登録'}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <TextField
            label="イベントコード"
            size="small"
            required
            value={form.event_code}
            onChange={set('event_code')}
            disabled={isEdit}
            helperText={isEdit ? '申込サイトと連動するため変更できません' : '例 YEP2027 (登録後は変更不可)'}
            sx={fieldSx}
          />
          <TextField
            label="イベント種別"
            size="small"
            value={form.event_type}
            onChange={set('event_type')}
            placeholder="忘年会 / セミナー / 交流会 等"
            sx={fieldSx}
          />
          <TextField
            label="イベント名"
            size="small"
            required
            value={form.event_name}
            onChange={set('event_name')}
            sx={{ ...fieldSx, gridColumn: '1 / -1' }}
          />
          <TextField
            label="開催日"
            size="small"
            type="date"
            value={form.event_date}
            onChange={set('event_date')}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            label="会場"
            size="small"
            value={form.venue}
            onChange={set('venue')}
            sx={fieldSx}
          />
          <TextField
            label="当日会費 (税込)"
            size="small"
            type="number"
            value={form.fee_regular}
            onChange={set('fee_regular')}
            sx={fieldSx}
          />
          <TextField
            label="事前振込会費 (税込)"
            size="small"
            type="number"
            value={form.fee_prepaid}
            onChange={set('fee_prepaid')}
            sx={fieldSx}
          />
          <TextField
            label="申込受付開始日"
            size="small"
            type="date"
            value={form.entry_start}
            onChange={set('entry_start')}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            label="申込受付終了日"
            size="small"
            type="date"
            value={form.entry_end}
            onChange={set('entry_end')}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          <TextField
            label="配置のグループの呼び名"
            size="small"
            value={form.group_unit_label}
            onChange={set('group_unit_label')}
            placeholder="卓 / 組 / レーン"
            helperText="席次表・組合表・投順表の見出し (例 卓)"
            sx={fieldSx}
          />
          <TextField
            label="配置の順番の呼び名"
            size="small"
            value={form.position_unit_label}
            onChange={set('position_unit_label')}
            placeholder="席 / 打順 / 投順"
            helperText="グループ内の順番の見出し (例 席)"
            sx={fieldSx}
          />
          <TextField
            select
            label="成績の形式"
            size="small"
            value={form.result_format_code}
            onChange={set('result_format_code')}
            helperText="順位やスコアを記録するイベントで選択します。成績の登録後は変更できません"
            sx={{ ...fieldSx, gridColumn: '1 / -1' }}
          >
            <MenuItem value="" sx={{ fontSize: '13px' }}>
              記録しない
            </MenuItem>
            {resultFormats.map((f) => (
              <MenuItem key={f.format_code} value={f.format_code} sx={{ fontSize: '13px' }}>
                {f.format_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Chat通知先 Webhook URL"
            size="small"
            type="url"
            value={form.chat_webhook_url}
            onChange={set('chat_webhook_url')}
            placeholder="https://chat.googleapis.com/v1/spaces/…"
            helperText="実行委員会スペースの Incoming Webhook。配信予定の削除依頼などの通知先になります"
            sx={{ ...fieldSx, gridColumn: '1 / -1' }}
          />
          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />}
            label={<Typography sx={{ fontSize: '13px' }}>公開中 (申込受付対象)</Typography>}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || form.event_name.trim() === '' || (!isEdit && form.event_code.trim() === '')}
          startIcon={<Icons.Save />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          {saving ? '保存中...' : '保存する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EventList() {
  const { events, resultFormats, loading, saving, error, saveEvent } = useEventList();
  const navigate = useNavigate();
  const notify = useNotification();
  const { permissionData } = usePermissions();
  // イベントマスタの登録・編集はキー保有者 (管理者) のみに出す。閲覧はメニュー権限側で担保
  const canEditMaster = permissionData.includes('event.master.edit');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EventListRow | null>(null);

  const openCreate = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };
  const openEdit = (row: EventListRow) => {
    setEditTarget(row);
    setDialogOpen(true);
  };

  const handleSave = async (data: EventUpsertInput) => {
    const result = await saveEvent(data);
    if (result.ok) {
      notify.success(data.id ? 'イベントを更新しました' : 'イベントを登録しました');
      setDialogOpen(false);
    } else {
      notify.error(result.error || '保存に失敗しました');
    }
  };

  if (loading) return <LoadingView message="イベント一覧を取得中..." height="80vh" />;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' }}>
      <Box sx={{ width: '92%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* 見出し + 新規登録 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
              <Icons.EventAvailable /> イベント管理
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569' }}>
              イベントを選択すると申込・参加者・入金・協賛の状況を確認できます
            </Typography>
          </Box>
          {canEditMaster && (
            <Fab variant="extended" size="small" onClick={openCreate} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Add />
              イベントを新規登録
            </Fab>
          )}
        </Box>

        {error && <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{error}</Alert>}

        {/* イベントカード一覧 */}
        {events.length === 0 && !error ? (
          <Box sx={{ mt: 4, p: 5, border: '2px dashed #cbd5e1', borderRadius: '12px', textAlign: 'center' }}>
            <Typography color="textSecondary">
              {canEditMaster ? 'イベントが未登録です。「イベントを新規登録」から作成してください。' : 'イベントが未登録です。'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {events.map((ev) => (
              <Card
                key={ev.id}
                onClick={() => navigate(`/event-management/${ev.id}`)}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                  '&:hover': { boxShadow: '0 6px 16px -4px rgb(0 0 0 / 0.15)', transform: 'translateY(-2px)' },
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* 上段 (名称 + 状態 + 編集) */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.5 }}>
                        {ev.event_type && (
                          <Chip size="small" label={ev.event_type} sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#eff6ff', color: '#3b82f6' }} />
                        )}
                        {ev.is_active && (
                          <Chip size="small" label="公開中" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669' }} />
                        )}
                        <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>{ev.event_code}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#1e293b', lineHeight: 1.3 }}>
                        {ev.event_name}
                      </Typography>
                    </Box>
                    {canEditMaster && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(ev);
                        }}
                        sx={{ color: '#94a3b8', '&:hover': { color: blueGrey[600] } }}
                      >
                        <Icons.Edit sx={{ fontSize: '16px' }} />
                      </IconButton>
                    )}
                  </Box>

                  {/* 中段 (開催情報) */}
                  <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    <Typography sx={{ fontSize: '12px', color: '#475569' }}>
                      開催日 {ev.event_date ?? '未定'}{ev.venue ? `／${ev.venue}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#475569' }}>
                      会費 当日 {yen(ev.fee_regular)}・事前振込 {yen(ev.fee_prepaid)}
                    </Typography>
                    {(ev.entry_start || ev.entry_end) && (
                      <Typography sx={{ fontSize: '12px', color: '#475569' }}>
                        受付期間 {ev.entry_start ?? ''} 〜 {ev.entry_end ?? ''}
                      </Typography>
                    )}
                  </Box>

                  {/* 下段 (申込サマリー) */}
                  <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid #f1f5f9', display: 'flex', gap: 2 }}>
                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                      申込 <strong style={{ color: '#1e293b' }}>{ev.entry_count.toLocaleString()}</strong> 件
                      (出席 {ev.attend_count.toLocaleString()})
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                      参加者 <strong style={{ color: '#1e293b' }}>{ev.participant_count.toLocaleString()}</strong> 名
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <EventEditDialog
        open={dialogOpen}
        initial={editTarget}
        resultFormats={resultFormats}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </Box>
  );
}
